import { useEffect, useState } from 'react';
import { Card, Input, Select, Slider, InputNumber, Switch, Button, Tooltip, message } from 'antd';
import { DeleteOutlined, CopyOutlined, QuestionCircleOutlined, RobotOutlined } from '@ant-design/icons';
import { useLanguage } from '@/hooks/useLanguage';
import { BotAvatar } from '@/components/common/BotAvatar';
import { lookupModel } from '../services/costEngine';
import { chatbotPickerService, type ChatbotPickerOption } from '../services/chatbotPickerService';
import type { AgentProfile, PricingPayload } from '../types';

interface Props {
  agent: AgentProfile;
  pricing: PricingPayload;
  onUpdate: (updated: AgentProfile) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  canDelete: boolean;
}

function buildModelOptions(pricing: PricingPayload) {
  const byProvider: Record<string, { label: string; value: string }[]> = {};
  for (const m of pricing.models) {
    if (!byProvider[m.provider_name]) byProvider[m.provider_name] = [];
    // Compact label — full pricing detail shown in the summary line beneath the dropdown.
    byProvider[m.provider_name].push({
      label: `${m.name}  ·  $${m.input_per_million}/$${m.output_per_million}`,
      value: m.id,
    });
  }
  return Object.entries(byProvider).map(([provider, options]) => ({ label: provider, options }));
}

export function AgentProfileCard({ agent, pricing, onUpdate, onDelete, onDuplicate, canDelete }: Props) {
  const { t } = useLanguage();
  const model = lookupModel(pricing, agent.model);
  const modelOptions = buildModelOptions(pricing);

  /* Chatbot picker — load once on mount; lazy in case the user never opens
     the dropdown (cheap call but still). */
  const [chatbots, setChatbots] = useState<ChatbotPickerOption[]>([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  useEffect(() => {
    chatbotPickerService.listChatbots()
      .then(setChatbots)
      .catch(() => {/* picker is optional; ignore failure */});
  }, []);

  function set<K extends keyof AgentProfile>(key: K, value: AgentProfile[K]) {
    onUpdate({ ...agent, [key]: value });
  }

  /* When the user picks a chatbot, fetch its profile and overwrite the THREE
     token fields (system prompt, avg user msg, avg assistant) — leave every
     other slider alone. Also record the chatbot's identity + avatar so the
     card can render its avatar on the left side. */
  async function applyChatbotProfile(chatbotId: string | undefined) {
    if (!chatbotId) {
      // Clear breadcrumb (avatar disappears) when user un-picks
      onUpdate({
        ...agent,
        basedOnChatbotId: undefined,
        basedOnChatbotName: undefined,
        basedOnAvatarColor: null,
        basedOnAvatarVariant: null,
      });
      return;
    }
    setPickerLoading(true);
    try {
      const p = await chatbotPickerService.getProfile(chatbotId);
      onUpdate({
        ...agent,
        systemPromptTokens: p.system_prompt_tokens,
        avgUserMessageTokens: p.avg_user_msg_tokens,
        avgAssistantTokens: p.avg_assistant_tokens,
        basedOnChatbotId: p.chatbot_id,
        basedOnChatbotName: p.name,
        basedOnAvatarColor: p.avatar_color,
        basedOnAvatarVariant: p.avatar_variant,
      });
      message.success(t('costSimulator.agent.pickerApplied', {
        name: p.name, sysTokens: p.system_prompt_tokens,
        user: p.avg_user_msg_tokens, assistant: p.avg_assistant_tokens,
      }));
    } catch {
      message.error(t('common.error'));
    } finally {
      setPickerLoading(false);
    }
  }

  return (
    <Card
      size="small"
      style={{ marginBlockEnd: 12 }}
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          {/* Avatar appears on the LEFT when this agent was loaded from an
              existing chatbot via the picker. Acts as a visual hint that the
              token defaults came from real DB stats, not generic numbers. */}
          {agent.basedOnChatbotId && (
            <BotAvatar
              name={agent.basedOnChatbotName || agent.name}
              size={28}
              colorOverride={agent.basedOnAvatarColor ?? undefined}
              variantOverride={agent.basedOnAvatarVariant ?? undefined}
            />
          )}
          <Input dir="auto"
            value={agent.name}
            onChange={e => set('name', e.target.value)}
            variant="borderless"
            style={{ fontWeight: 600, paddingInlineStart: 0, flex: 1, minWidth: 0 }}
          />
        </div>
      }
      extra={
        <div style={{ display: 'flex', gap: 4 }}>
          <Tooltip title={t('costSimulator.agent.duplicate')}>
            <Button type="text" icon={<CopyOutlined />} onClick={onDuplicate} size="small" />
          </Tooltip>
          {canDelete && (
            <Tooltip title={t('costSimulator.agent.delete')}>
              <Button type="text" danger icon={<DeleteOutlined />} onClick={onDelete} size="small" />
            </Tooltip>
          )}
        </div>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' }}>

        {/* Chatbot picker — auto-fills the 3 token fields from a real chatbot's
            DB stats. User and assistant averages computed STRICTLY separately. */}
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={{ fontSize: 12, opacity: 0.65, display: 'flex', alignItems: 'center', gap: 4 }}>
            <RobotOutlined style={{ opacity: 0.7 }} />
            {t('costSimulator.agent.pickerLabel')}
            <Tooltip title={t('costSimulator.agent.pickerTooltip')}>
              <QuestionCircleOutlined style={{ opacity: 0.5 }} />
            </Tooltip>
          </label>
          <Select
            placeholder={t('costSimulator.agent.pickerPlaceholder')}
            allowClear
            showSearch
            optionFilterProp="label"
            loading={pickerLoading}
            disabled={chatbots.length === 0}
            value={agent.basedOnChatbotId ?? undefined}
            onChange={applyChatbotProfile}
            style={{ width: '100%', marginBlockStart: 2 }}
            size="small"
            options={chatbots.map(c => ({
              label: `${c.name}  ·  ${c.system_prompt_tokens.toLocaleString()} tok system prompt`,
              value: c.chatbot_id,
            }))}
          />
        </div>

        {/* Model */}
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={{ fontSize: 12, opacity: 0.65 }}>{t('costSimulator.agent.model')}</label>
          <Select
            value={agent.model}
            onChange={v => set('model', v)}
            options={modelOptions}
            style={{ width: '100%', marginBlockStart: 2 }}
            size="small"
            showSearch
            optionFilterProp="label"
          />
          <div style={{ fontSize: 11, opacity: 0.6, marginBlockStart: 4, lineHeight: 1.5 }}>
            <strong>{model.provider_name}</strong> · per 1M tokens:&nbsp;
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {t('costSimulator.priceInput')} <strong>${model.input_per_million}</strong> · {t('costSimulator.priceOutput')} <strong>${model.output_per_million}</strong> · {t('costSimulator.priceCachedInput')} <strong>${model.cached_input_per_million}</strong>
            </span>
          </div>
        </div>

        {/* Active users — log-feeling: step 1 up to 100, step 10 up to 1000, step 100 above */}
        <LogSliderField
          label={t('costSimulator.agent.activeUsers')}
          min={1} max={100_000}
          value={agent.activeUsersPerMonth}
          onChange={v => set('activeUsersPerMonth', v)}
          tooltip={t('costSimulator.agent.activeUsersTooltip')}
        />

        {/* Conversations per user */}
        <SliderField
          label={t('costSimulator.agent.convsPerUser')}
          min={1} max={500}
          value={agent.conversationsPerUserPerMonth}
          onChange={v => set('conversationsPerUserPerMonth', v)}
        />

        {/* Messages per conversation */}
        <SliderField
          label={t('costSimulator.agent.msgsPerConv')}
          min={2} max={200} step={2}
          value={agent.messagesPerConversation}
          onChange={v => set('messagesPerConversation', v)}
          tooltip={t('costSimulator.agent.msgsTooltip')}
        />

        {/* Avg user message tokens — min=1 because real user messages can be
            very short (the JewishPro real average is 15 tokens; previous min=20
            was an arbitrary guess that rejected the calibrated default). */}
        <SliderField
          label={t('costSimulator.agent.userTokens')}
          min={1} max={3_000}
          value={agent.avgUserMessageTokens}
          onChange={v => set('avgUserMessageTokens', v)}
        />

        {/* Avg assistant tokens — min=1 same reasoning (some agents return
            single-line confirmations). */}
        <SliderField
          label={t('costSimulator.agent.assistantTokens')}
          min={1} max={5_000}
          value={agent.avgAssistantTokens}
          onChange={v => set('avgAssistantTokens', v)}
        />

        {/* System prompt tokens */}
        <SliderField
          label={t('costSimulator.agent.systemPromptTokens')}
          min={0} max={20_000} step={100}
          value={agent.systemPromptTokens}
          onChange={v => set('systemPromptTokens', v)}
          tooltip={t('costSimulator.agent.systemPromptTooltip')}
        />

        {/* Files count */}
        <SliderField
          label={t('costSimulator.agent.filesCount')}
          min={0} max={500}
          value={agent.filesCount}
          onChange={v => set('filesCount', v)}
        />

        {/* Avg file size */}
        {agent.filesCount > 0 && (
          <SliderField
            label={t('costSimulator.agent.avgFileSizeMb')}
            min={0.1} max={50} step={0.1}
            value={agent.avgFileSizeMb}
            onChange={v => set('avgFileSizeMb', v)}
            suffix="MB"
          />
        )}

        {/* RAG queries per conv */}
        {agent.filesCount > 0 && (
          <SliderField
            label={t('costSimulator.agent.ragQueries')}
            min={0} max={20} step={1}
            value={agent.ragQueriesPerConversation}
            onChange={v => set('ragQueriesPerConversation', v)}
            tooltip={t('costSimulator.agent.ragQueriesTooltip')}
          />
        )}

        {/* Monthly file churn % — affects recurring file-processing cost */}
        {agent.filesCount > 0 && (
          <SliderField
            label={t('costSimulator.agent.fileChurn')}
            min={0} max={30} step={1}
            value={agent.fileChurnRatePct ?? 5}
            onChange={v => set('fileChurnRatePct', v)}
            tooltip={t('costSimulator.agent.fileChurnTooltip')}
            suffix="%"
          />
        )}

        {/* Insights tools bound */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, gridColumn: '1 / -1' }}>
          <Switch size="small" checked={agent.insightsToolsBound} onChange={v => set('insightsToolsBound', v)} />
          <label style={{ fontSize: 12 }}>
            {t('costSimulator.agent.insightsToolsBound')}{' '}
            <Tooltip title={t('costSimulator.agent.insightsToolsTooltip')}>
              <QuestionCircleOutlined style={{ opacity: 0.5 }} />
            </Tooltip>
          </label>
        </div>

      </div>
    </Card>
  );
}

// Reusable slider+inputnumber field
interface SliderFieldProps {
  label: string;
  min: number;
  max: number;
  step?: number;
  value: number;
  onChange: (v: number) => void;
  tooltip?: string;
  suffix?: string;
}

function SliderField({ label, min, max, step = 1, value, onChange, tooltip, suffix }: SliderFieldProps) {
  return (
    <div>
      <label style={{ fontSize: 12, opacity: 0.65, display: 'flex', alignItems: 'center', gap: 4 }}>
        {label}
        {tooltip && (
          <Tooltip title={tooltip}>
            <QuestionCircleOutlined style={{ opacity: 0.5 }} />
          </Tooltip>
        )}
      </label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBlockStart: 2 }}>
        <Slider
          min={min} max={max} step={step} value={value}
          onChange={onChange}
          style={{ flex: 1 }}
          tooltip={{ formatter: v => `${v}${suffix ? ' ' + suffix : ''}` }}
        />
        <InputNumber
          min={min} max={max} step={step} value={value}
          /* Accept null briefly so the user can clear and re-type a new number
             without the field snapping back to min on every keystroke. Ant
             clamps on blur via the min/max props. */
          onChange={v => { if (typeof v === 'number' && !Number.isNaN(v)) onChange(v); }}
          size="small" style={{ width: 90 }}
          formatter={(v) => (v === undefined || v === null || String(v) === '') ? '' : `${v}${suffix ? ' ' + suffix : ''}`}
          parser={s => {
            const cleaned = String(s ?? '').replace(/[^\d.\-]/g, '');
            const n = Number(cleaned);
            return Number.isNaN(n) ? (min as any) : (n as any);
          }}
        />
      </div>
    </div>
  );
}

/**
 * Log-scale slider — slider position 0..1000 maps to a value via log10 between
 * `min` and `max`. This means dragging covers small numbers in fine detail and
 * large numbers in coarse detail, instead of the linear case where every pixel
 * jumps hundreds of users.
 */
function LogSliderField({ label, min, max, value, onChange, tooltip }: Omit<SliderFieldProps, 'step' | 'suffix'>) {
  const SCALE = 1000;
  const logMin = Math.log10(Math.max(1, min));
  const logMax = Math.log10(max);
  const valueToPos = (v: number) => Math.round(SCALE * (Math.log10(Math.max(1, v)) - logMin) / (logMax - logMin));
  const posToValue = (p: number) => {
    const v = Math.pow(10, logMin + (p / SCALE) * (logMax - logMin));
    // Snap to nice round numbers
    if (v < 10) return Math.round(v);
    if (v < 100) return Math.round(v / 5) * 5;
    if (v < 1_000) return Math.round(v / 10) * 10;
    if (v < 10_000) return Math.round(v / 100) * 100;
    return Math.round(v / 1_000) * 1_000;
  };

  return (
    <div>
      <label style={{ fontSize: 12, opacity: 0.65, display: 'flex', alignItems: 'center', gap: 4 }}>
        {label}
        {tooltip && (
          <Tooltip title={tooltip}>
            <QuestionCircleOutlined style={{ opacity: 0.5 }} />
          </Tooltip>
        )}
      </label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBlockStart: 2 }}>
        <Slider
          min={0} max={SCALE}
          value={valueToPos(value)}
          onChange={p => onChange(posToValue(p))}
          style={{ flex: 1 }}
          tooltip={{ formatter: p => (p == null ? '' : posToValue(p).toLocaleString()) }}
        />
        <InputNumber
          min={min} max={max}
          value={value}
          onChange={v => { if (typeof v === 'number' && !Number.isNaN(v)) onChange(v); }}
          size="small" style={{ width: 100 }}
          formatter={(v) => (v === undefined || v === null || String(v) === '') ? '' : Number(v).toLocaleString()}
          parser={s => {
            const cleaned = String(s ?? '').replace(/[^\d]/g, '');
            return (cleaned === '' ? min : Number(cleaned)) as any;
          }}
        />
      </div>
    </div>
  );
}
